/**
 * SendHeap AI Arama widget'ının giriş noktası.
 *
 * Üç kullanım biçimi — mağaza kendi ihtiyacına göre birini seçer:
 *
 * 1. **Tek script etiketi** — `<script src="…" data-search-key="…" data-trigger="#q">`
 *    Başka hiçbir şey yazmaya gerek yok; widget kendini kurar.
 * 2. **`init(options)`** — hazır arayüz, JS'ten tam kontrol (metinler,
 *    `renderItem`, `onSelect` gibi attribute'a sığmayan ayarlar için).
 * 3. **`createClient(options)`** — yalnızca veri. UI'ı geliştirici yazar,
 *    bizim CSS'imiz/DOM'umuz sayfasına hiç girmez.
 *
 * Bundle vanilla: framework yok, bağımlılık yok. `<script>` ile yüklendiğinde
 * `window.SendheapSearch` altına yerleşir.
 */

import { autoInit } from "./auto-init";
import {
  createSearchClient,
  type ISearchClient,
  type ISearchClientOptions,
} from "./client";
import type { IInitOptions } from "./types";
import { createWidgetUi, type IWidgetUi } from "./ui";

/** Build sırasında esbuild `define` ile gerçek sürüme çevirir. */
declare const __WIDGET_VERSION__: string;

export const version =
  typeof __WIDGET_VERSION__ === "string" ? __WIDGET_VERSION__ : "dev";

export interface IWidgetInstance extends IWidgetUi {
  client: ISearchClient;
}

/**
 * Hazır arayüzü kurar.
 *
 * ```js
 * SendheapSearch.init({
 *   searchKey: "…",
 *   trigger: "#search-input",
 *   currency: "TRY",
 * });
 * ```
 */
export const init = (options: IInitOptions): IWidgetInstance => {
  const client = createSearchClient(options);
  const ui = createWidgetUi({ ...options, client });
  return { ...ui, client };
};

export const createClient = createSearchClient;

export interface ISendheapSearchGlobal {
  version: string;
  createClient: (options: ISearchClientOptions) => ISearchClient;
  init: (options: IInitOptions) => IWidgetInstance;
  /**
   * `data-search-key` ile otomatik kurulan örnek. Tek etiketle kurulup sonradan
   * programatik erişim gerekirse (`instance.open()`) buradan alınır.
   * Elle `init` çağrıldıysa `null` kalır.
   */
  instance: IWidgetInstance | null;
}

const api: ISendheapSearchGlobal = {
  version,
  createClient: createSearchClient,
  init,
  instance: null,
};

export type {
  ISearchClient,
  ISearchClientOptions,
  IWatchHandlers,
  IWatchHandle,
  SearchState,
  AiSearchQueryResponse,
  IAiSearchQueryResult,
} from "./client";
export { AiSearchQueryError } from "./client";
export type {
  IWidgetUi,
  IWidgetUiOptions,
  IWidgetTexts,
  TWidgetTheme,
} from "./ui";
export type { IInitOptions } from "./types";

// `<script>` ile yüklendiğinde global'e bağlan. Aynı sayfaya iki kez eklenirse
// ikinci yükleme mevcut örneği ezmesin — mağaza tema/eklenti çakışmalarında bu olur.
declare global {
  interface Window {
    SendheapSearch?: ISendheapSearchGlobal;
  }
}

if (typeof window !== "undefined") {
  if (!window.SendheapSearch) {
    window.SendheapSearch = api;
    // Yalnızca ilk yükleme otomatik kurulum denesin; ikinci script etiketi
    // aynı sayfada ikinci bir modal açmasın.
    autoInit(init, (instance) => {
      api.instance = instance as IWidgetInstance;
    });
  }
}

export default api;
